import { FieldValues } from 'react-hook-form';
import {
	PerilCreateRequest,
	PerilCreateRequestHelpers,
	PerilUpdateRequest,
} from '../../../types/policy';
import {
	PERIL_FIELD_NAMES,
	PERIL_FIELD_UPDATE_VALUES,
	PERIL_FIELD_VALUES,
} from '../data/constants';
import { DirtyFields, FormBuilder, FormShape } from '../types';
import filterObjectByKeys from './filterObjectByKeys';
import updateRequestBuilder from './updateRequestBuilder';

const PERIL_SPECIAL_FIELDS = [
	PERIL_FIELD_NAMES.indemnityPerItemType,
	PERIL_FIELD_NAMES.indemnityPerItem,
	PERIL_FIELD_NAMES.indemnityPerEventType,
	PERIL_FIELD_NAMES.indemnityPerEvent,
	PERIL_FIELD_NAMES.indemnityPerAggregateType,
	PERIL_FIELD_NAMES.indemnityPerAggregate,
	PERIL_FIELD_NAMES.lumpSumPaymentType,
	PERIL_FIELD_NAMES.lumpSumPayment,
	PERIL_FIELD_NAMES.deductibleType,
	PERIL_FIELD_NAMES.deductible,
];

const createPerilRequestBuilder = (
	dirtyFields: { [key: string]: DirtyFields },
	formValues: FieldValues,
	fields: FormShape<FormBuilder>,
	key: string,
	timezone: string,
	packageAllFields = false,
	isEndorsementOrRenewal = false,
) => {
	let updateObject = {} as PerilCreateRequest & PerilCreateRequestHelpers;

	if (Object.keys(dirtyFields[key] || {}).length > 0) {
		let newUpdatedObject;
		const currDirtyFields = { [key]: dirtyFields[key] || {} };
		const currFormValues = { [key]: formValues[key] || {} };
		const currFields = {
			[key as keyof FormBuilder]: fields[key] || {},
		} as FormShape<FormBuilder>;

		Object.keys(currDirtyFields[key]).forEach((field) => {
			if (!packageAllFields && currDirtyFields[key][field] === false) {
				return;
			}
			switch (field) {
				case PERIL_FIELD_NAMES.indemnityPerItemType:
					if (currFormValues[key][field] === PERIL_FIELD_VALUES.unlimited) {
						updateObject.indemnityPerItem = PERIL_FIELD_UPDATE_VALUES.unlimited;
					} else if (currFormValues[key][field] === PERIL_FIELD_VALUES.unset) {
						updateObject.removeIndemnityPerItem = true;
					} else {
						updateObject.indemnityPerItem =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerItem];
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerEventType:
					if (currFormValues[key][field] === PERIL_FIELD_VALUES.unlimited) {
						updateObject.indemnityPerEvent =
							PERIL_FIELD_UPDATE_VALUES.unlimited;
					} else if (currFormValues[key][field] === PERIL_FIELD_VALUES.unset) {
						updateObject.removeIndemnityPerEvent = true;
					} else {
						updateObject.indemnityPerEvent =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerEvent];
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerAggregateType:
					if (currFormValues[key][field] === PERIL_FIELD_VALUES.unlimited) {
						updateObject.indemnityInAggregate =
							PERIL_FIELD_UPDATE_VALUES.unlimited;
					} else if (currFormValues[key][field] === PERIL_FIELD_VALUES.unset) {
						updateObject.removeIndemnityInAggregate = true;
					} else {
						updateObject.indemnityInAggregate =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerAggregate];
					}
					break;
				case PERIL_FIELD_NAMES.lumpSumPaymentType:
					if (
						currFormValues[key][PERIL_FIELD_NAMES.lumpSumPaymentType] ===
						PERIL_FIELD_VALUES.set
					) {
						updateObject.lumpSumPayment =
							currFormValues[key][PERIL_FIELD_NAMES.lumpSumPayment];
					} else {
						updateObject.removeLumpSumPayment = true;
					}
					break;
				case PERIL_FIELD_NAMES.deductibleType:
					if (
						currFormValues[key][PERIL_FIELD_NAMES.deductibleType] ===
						PERIL_FIELD_VALUES.set
					) {
						updateObject.deductible =
							currFormValues[key][PERIL_FIELD_NAMES.deductible];
					} else {
						updateObject.removeDeductible = true;
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerItem:
					if (!currDirtyFields[key][PERIL_FIELD_NAMES.indemnityPerItemType]) {
						updateObject.indemnityPerItem =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerItem];
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerEvent:
					if (!currDirtyFields[key][PERIL_FIELD_NAMES.indemnityPerEventType]) {
						updateObject.indemnityPerEvent =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerEvent];
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerAggregate:
					if (
						!currDirtyFields[key][PERIL_FIELD_NAMES.indemnityPerAggregateType]
					) {
						updateObject.indemnityInAggregate =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerAggregate];
					}
					break;
				case PERIL_FIELD_NAMES.deductible:
					if (!currDirtyFields[key][PERIL_FIELD_NAMES.deductibleType]) {
						updateObject.deductible =
							currFormValues[key][PERIL_FIELD_NAMES.deductible];
					}
					break;
				case PERIL_FIELD_NAMES.lumpSumPayment:
					if (!currDirtyFields[key][PERIL_FIELD_NAMES.lumpSumPaymentType]) {
						updateObject.lumpSumPayment =
							currFormValues[key][PERIL_FIELD_NAMES.lumpSumPayment];
					}
					break;
				default:
					break;
			}
		});

		const filteredCurrFormValues = {
			[key]: filterObjectByKeys(currFormValues[key], PERIL_SPECIAL_FIELDS),
		};

		const filteredDirtyFields = {
			[key]: filterObjectByKeys(currDirtyFields[key], PERIL_SPECIAL_FIELDS),
		};

		const currFieldsWithAddedFieldGroups = {
			...currFields,
			[key]: {
				...currFields[key],
				addFieldGroups: currFields[key].fieldGroups,
			},
		};

		newUpdatedObject = updateRequestBuilder<PerilUpdateRequest>(
			filteredDirtyFields,
			filteredCurrFormValues,
			currFieldsWithAddedFieldGroups,
			key,
			timezone,
			packageAllFields,
			isEndorsementOrRenewal,
		);
		updateObject = { ...updateObject, ...newUpdatedObject };
	}

	const { fieldGroups, fieldValues, addFieldGroups } = updateObject;
	if (fieldGroups && fieldGroups.length === 0) {
		delete updateObject.fieldGroups;
	}
	if (fieldValues && Object.keys(fieldValues).length === 0) {
		delete updateObject.fieldValues;
	}

	if (addFieldGroups) {
		// Remove any undefined field groups
		updateObject.addFieldGroups = updateObject.addFieldGroups.filter(
			(fieldGroup) => fieldGroup,
		);
	}

	return updateObject;
};

export default createPerilRequestBuilder;
